import { defineStore } from "pinia";
import {
  AnalyticsV2ChartType,
  AnalyticsV2ElementType,
  ColumnCategory,
  TableHeaderCellOrderMode,
  TagEntityType,
} from "@/models/etc/types";
import {
  CommonHttp,
  PersonEditRequest,
  SingleIdRequest,
} from "@/models/interfaces/http/common.http.request";
import { rootStore } from "./index";
import { AddressInputComponentOut, AddressInputContainer, RenameAsker, TagEditStatusAsker } from "@/models/interfaces/entity-settings";
import { BaseListItem, getIdentityKind } from "@/components/common/base-list/interfaces/ListTypes";
import { PaginationProps } from "@/models/base/common.selector";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { TagContainer } from "@/models/interfaces/tags/tag-models";
import _ from "lodash";
import { AnalyticsActionRecordsLogFetchRequest, AnalyticsActionType, AnalyticsChartIdentity, AnalyticsElementIdentity, AnalyticsElementKind, AnalyticsFolderIdentity } from "@/models/interfaces/entity-settings/charts/charts";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { generalStore } from "./general";

/**
 * auxillary methods store; contains common methods for all the system,
 * e.g. tag selector, users selector, clients selector, etc.
 */

const API_URL = process.env.VUE_APP_API_URL;
// for matching string values that are in a wrong keyboard layout
const ruLayout = require("convert-layout/ru");

export type AuxState = {
  _COUNT_: number;
  table_loading: boolean;
  container_cache: Map<TagEntityType, TagContainer>;
  address_input_container_cache: AddressInputComponentOut[];

  // Параметры последнего раскрытия таблицы нагрузки: сектор или точка кривой.
  // Нужны мультиэдиту комментариев, чтобы бэкенд восстановил набор активностей.
  last_workload_asker?: AnalyticsWorkloadChartTableAskerBase;
  last_workload_chart_type?: AnalyticsV2ChartType;

  // Map<entityKey, queryStruct>
  reqQueue: Map<string, AbortController>;
};

export interface PersonInfo {
  Post: string;
  Address: string[];
  Contacts: string;
  Tags: string[];
  CreateTime: Date;
  Creator: string;
  Changelog: any[];
  ConnectedClients: CommonEntityRecord[];
  ConnectedProjects: CommonEntityRecord[];
}

export interface AnalyticsChartSectorTableOrdering {
  ColumnCategory: ColumnCategory;
  OrderMode: TableHeaderCellOrderMode; // Ascending / Descending
  OrderingEntityIds: number[];
}

export interface AnalyticsWorkloadChartTableAskerBase {
  Count: number;
  Offset: number;
  Ordering: AnalyticsChartSectorTableOrdering | null;
}

export interface AnalyticsWorkloadChartSectorTableAsker
  extends AnalyticsWorkloadChartTableAskerBase {
  ChartId: number;
  WorkerIds: number[];
}
export interface AnalyticsChartSectorTableAsker {
  ChartIdentity: AnalyticsChartIdentity;
  ChartElementId: number;
  EntityIds: number[];
}
export interface AnalyticsWorkloadCurvePointTableAsker extends AnalyticsWorkloadChartTableAskerBase {
  CurveId: number;
  StartDate: Date;
  EndDate: Date;
}

export const auxStore = defineStore({
  id: "auxStore",
  state: () => ({
    _COUNT_: process.env.VUE_APP_RESULTS_COUNT,
    table_loading: false,
    container_cache: new Map(),
    address_input_container_cache: [],
    last_workload_asker: undefined,
    last_workload_chart_type: undefined,
    reqQueue: new Map()
  } as AuxState),

  actions: {
    abortAllPending() {
      this.reqQueue.forEach((controller) => controller.abort())
      this.reqQueue.clear()
    },

    setTableRowCount(val: number) {
      console.log('update '+val)
      this._COUNT_ = val
    },

    async initContainers() {
      const rootstore = rootStore()
      const http = new CommonHttp()

      // init tag container caches
      const cresponse = await http.get(`Tag/GetList/?type=${TagEntityType.Client}`)
      if (cresponse.status === 200) {
        this.container_cache.set(TagEntityType.Client, cresponse.data)
      }
      const presponse = await http.get(`Tag/GetList/?type=${TagEntityType.Project}`)
      if (presponse.status === 200) {
        this.container_cache.set(TagEntityType.Project, presponse.data)
      }
      const persresponse = await http.get(`Tag/GetList/?type=${TagEntityType.Person}`)
      if (persresponse.status === 200) {
        this.container_cache.set(TagEntityType.Person, persresponse.data)
      }
      /*const cfresponse = await http.get(`Tag/GetList/?type=${TagEntityType.ClientFilter}`)
      if (cfresponse.status === 200) {
        this.container_cache.set(TagEntityType.ClientFilter, cfresponse.data)
      }
      const pfresponse = await http.get(`Tag/GetList/?type=${TagEntityType.ProjectFilter}`)
      if (pfresponse.status === 200) {
        this.container_cache.set(TagEntityType.ProjectFilter, pfresponse.data)
      }*/
      const fresponse = await http.get(`Tag/GetList/?type=${TagEntityType.File}`)
      if (fresponse.status === 200) {
        this.container_cache.set(TagEntityType.File, fresponse.data)
      }
      const jresponse = await http.get(`Tag/GetList/?type=${TagEntityType.Job}`)
      if (jresponse.status === 200) {
        this.container_cache.set(TagEntityType.Job, jresponse.data)
      }
    },

    async getTagContainer() {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let t_type: TagEntityType = TagEntityType.Unknown

        switch (rootstore.generalMode) {
          case AnalyticsV2ElementType.Client:
            t_type = TagEntityType.Client;
            break;
          case AnalyticsV2ElementType.Project:
            t_type = TagEntityType.Project;
            break;
          case AnalyticsV2ElementType.File:
            t_type = TagEntityType.File;
            break;
          case AnalyticsV2ElementType.Person:
            t_type = TagEntityType.Person;
            break;
          case AnalyticsV2ElementType.Job:
            t_type = TagEntityType.Job;
            break;
          default:
            t_type = TagEntityType.Unknown;
            break;
        }

        const response = await http.get(`Tag/GetList/?type=${t_type}`);

        if (response.status === 200) {
          return response
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getFilterClients(filter: string, count: number, offset: number) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/GetFilterEntities/Clients/?filter=${filter}&count=${count}&offset=${offset}`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getGlobalFormulasList(identity: AnalyticsElementIdentity) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        // Source есть только у проекций, у выборок и списков его нет, поэтому параметр опускается.
        const source = identity.Source != null ? `&source=${identity.Source}` : ''
        const response = await http.get(
          `Analytics/General/GetGlobalTableFormulas?elementId=${identity.ElementId}&kind=${identity.Kind}${source}&destination=${identity.Destination}`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async downloadFile(fileId: any, label: string) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      http.get(`Files/Download/${fileId}`, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = label
        link.click()
        URL.revokeObjectURL(link.href)
      }).catch(console.error)

      return { data: undefined }
    },

    async downloadTableFile(arg: BaseListItem, label: string) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      http.post(`Analytics/Export/ElementTable`, {
        ElementId: arg.Id,
        Source: [0,1,2,3,4,5,6,7,8].includes(arg.SrcType) ? arg.SrcType : null,
        Destination: arg.DestType,
        Kind: getIdentityKind(arg)
      } as AnalyticsElementIdentity, { responseType: 'blob' })
      .then(response => {
        if (response.data) {
          const blob = new Blob([response.data])
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = label+'.xlsx'
          link.click()
          URL.revokeObjectURL(link.href)
        } else {
          return { data: undefined }
        }
      }).catch(console.error)

      return { data: undefined }
    },

    async downloadSectorTableFile(arg: BaseListItem, entityIds: number[], pag: PaginationProps, label: string) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      http.post(`Analytics/Export/ChartTable`, {
        ChartIdentity: {
          ChartId: arg.Id,
          ChartType: arg.elType === 2 ? AnalyticsV2ChartType.Linear : 
            (arg.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
          Destination: arg.DestType
        },
        EntityIds: entityIds,
        Ordering: {
          ColumnCategory: pag.sortCategory,
          OrderMode: pag.sortMode,
          OrderingEntityIds: Array.isArray(pag.OrderingEntityIds)
            ? pag.OrderingEntityIds
            : []
        } as AnalyticsChartSectorTableOrdering
      }, { responseType: 'blob' })
      .then(response => {
        if (response.data) {
          const blob = new Blob([response.data])
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = label+'.xlsx'
          link.click()
          URL.revokeObjectURL(link.href)
        } else {
          return { data: undefined }
        }
      }).catch(console.error)

      return { data: undefined }
    },

    async getSourceJobs(ids: number[]) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const request = {
        IdList: ids
      }
      try {
        const response = await http.post(`job/get-by-ids`, request);
        if (response.status === 200) {

          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getSourcePersons(ids: number[]) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const request = {
        IdList: ids
      }
      try {
        const response = await http.post(`persons/get-by-ids`, request);
        if (response.status === 200) {

          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getSourceTable(ids: number[]) {
      const rootstore = rootStore()
      const genstore = generalStore()

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.table_loading = true;
        let response = undefined;

        const params = {
          ProjectionIdentity: {
            ElementId: (genstore.selectedItem as BaseListItem).Id,
            Source: [7, 8].includes(
              (genstore.selectedItem as BaseListItem).SrcType
            )
              ? (genstore.selectedItem as BaseListItem).SrcType
              : null,
            Destination: (genstore.selectedItem as BaseListItem).DestType,
            Kind: getIdentityKind(genstore.selectedItem as BaseListItem),
          } as AnalyticsElementIdentity,
          InitialElementIds: ids
        }
        
        await http.post(`Analytics/Projections/GetTableResult/InitialProjectionElements`, params).then(res => {
          response = res
        }).catch(err => {
          console.log('error 238')
        });

        this.table_loading = false
        
        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          const { data } = response;
          return data
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.table_loading = false
      }

      this.table_loading = false
      return undefined
    },

    async getFilterProjects(filter: string, count: number, offset: number) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/GetFilterEntities/Projects/?filter=${filter}&count=${count}&offset=${offset}`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getFilterStringSets(filter: string, setId: number, count: number, offset: number) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.post(`Analytics/GetFilterEntities/StringSetValues`, {
          SetId: setId, // > 0
          Filter: filter, //null / string
          Count: count, // [0-100]
          Offset: offset // >=0
        });
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getFilterStages() {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/GetFilterEntities/Stages`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getFilterLists(filter: string, count: number, offset: number, excludeList: number, segment: number = -1) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/Lists?segment=${segment > -1 ? segment : rootstore.generalMode}&filter=${
          filter}&count=${count}&offset=${offset}`+(excludeList > 0 ? `&listIdToExclude=${excludeList}` : ``));
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getReportsList() {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/Reports/GetList`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getChartSectorTable(arg: any, pagination: any) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const entkey = arg.ChartIdentity.ChartType+'-'+arg.ChartIdentity.Destination+'-'+arg.ChartIdentity.ChartId;

        // abort all other entity/graph result connections because only one element can be displayed at a time
        Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
          if (key != entkey) {
            console.log('abort sector table conn '+key)
            this.reqQueue.get(key)?.abort();
            this.reqQueue.delete(key);
          }
        });

        this.table_loading = true;
        let response = undefined;

        const params = arg;
        params.Offset = +pagination.offset
        params.Count = +(pagination.count ? pagination.count : Number(this.getTableRowCount))

        const controller = new AbortController();
        this.reqQueue.set(entkey, controller);

        await http
          .post(`Analytics/Charts/GetSectorTableResult`, params, {
            signal: controller.signal,
          })
          .then((res) => {
            response = res;
            this.reqQueue.delete(entkey);
          })
          .catch((err) => {
            console.log("error 23");
            this.reqQueue.delete(entkey);
          });

        this.table_loading = false
        
        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.table_loading = false
      }

      this.table_loading = false
      return { data: undefined }
    },

    async getWorkLoadChartSectorTable(arg: any, pagination: any, ctype: AnalyticsV2ChartType) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const entkey = 'wload-'+(arg.ChartId ? arg.ChartId : arg.CurveId);

        // abort all other entity/graph result connections because only one element can be displayed at a time
        Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
          if (key != entkey) {
            console.log('abort wl sector table conn '+key)
            this.reqQueue.get(key)?.abort();
            this.reqQueue.delete(key);
          }
        });

        this.table_loading = true
        let response = undefined

        const params = arg;
        let back_url = "";

        switch (ctype) {
          case AnalyticsV2ChartType.Linear:
            back_url = `Analytics/Charts/Workload/GetTableResult/Linear`;
            break;
          case AnalyticsV2ChartType.Pie:
            back_url = `Analytics/Charts/Workload/GetTableResult/Pie`;
            break;
          case AnalyticsV2ChartType.Bar:
            back_url = `Analytics/Charts/Workload/GetTableResult/Bar`;
            break;
          default:
            break;
        }
        (params as AnalyticsWorkloadChartTableAskerBase).Offset = +pagination.offset;
        (params as AnalyticsWorkloadChartTableAskerBase).Count = +(pagination.count ? pagination.count : Number(this.getTableRowCount));

        // Через этот метод проходит любой запрос таблицы нагрузки, поэтому тут же
        // запоминаем, из какого сектора или точки кривой она построена.
        this.last_workload_asker = params as AnalyticsWorkloadChartTableAskerBase;
        this.last_workload_chart_type = ctype;

        const controller = new AbortController();
        this.reqQueue.set(entkey, controller);

        await http
          .post(back_url, params, { signal: controller.signal })
          .then((res) => {
            response = res;
            this.reqQueue.delete(entkey);
          })
          .catch((err) => {
            console.log("error 24");
            this.reqQueue.delete(entkey);
          });

        this.table_loading = false
        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.table_loading = false
      }

      this.table_loading = false
      return { data: undefined }
    },

    // contacts
    async getPersonInfo(arg: number) {
      const rootstore = rootStore()
      const http = new CommonHttp(true)
      try {
        const response = await http.get(`persons/getSingle/${arg}`);

        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.table_loading = false
      }

      return { data: undefined }
    },

    // contacts
    async entityHasSavedState(arg: BaseListItem) {
      const rootstore = rootStore()
      const http = new CommonHttp(true)
      let http_url = "";
      let params: any = {};

      switch (arg.elType) {
        case BaseItemTypes.SELECTION:
          params = {
            ElementId: arg.Id,
            Destination: arg.DestType,
            Kind: AnalyticsElementKind.Selection
          } as AnalyticsElementIdentity;
          http_url = "Analytics/General/ElementHasSavedState";
          break;
        case BaseItemTypes.LIST:
          params = {
            ElementId: arg.Id,
            Destination: arg.DestType,
            Kind: AnalyticsElementKind.List
          } as AnalyticsElementIdentity;
          http_url = "Analytics/General/ElementHasSavedState";
          break;
        case BaseItemTypes.PROJECTION:
          params = {
            ElementId: arg.Id,
            Source: arg.SrcType,
            Destination: arg.DestType,
            Kind: AnalyticsElementKind.Projection
          } as AnalyticsElementIdentity;
          http_url = "Analytics/General/ElementHasSavedState";
          break;
        case BaseItemTypes.GRAPH:
        case BaseItemTypes.PIE_CHART:
        case BaseItemTypes.BAR_CHART:
          params = {
            ChartId: arg.Id,
            ChartType: arg.elType === 2 ? AnalyticsV2ChartType.Linear : 
              (arg.elType === 3 ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
            Destination: arg.DestType
          } as AnalyticsChartIdentity;
          http_url = "Analytics/Charts/HasForceRecalcDate";
          break;
        default:
          http_url = ""
          break;
      }
      
      const entkey = 'hasSavedState'
      this.reqQueue.get(entkey)?.abort()
      this.reqQueue.delete(entkey)
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      try {
        const response = await http.post(http_url, params, { signal: controller.signal });
        this.reqQueue.delete(entkey)
        
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        this.reqQueue.delete(entkey)
        if ((e as any)?.name === 'CanceledError' || (e as any)?.code === 'ERR_CANCELED') return undefined
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getAnalyticsElementInfo(arg: BaseListItem, opts?: { readCount?: number; writeCount?: number }) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT, true)
      let http_url = "";
      let params: any = {};

      const writeCount = opts?.writeCount ?? 8;
      const readCount = opts?.readCount ?? 8;

      switch (arg.elType) {
        case BaseItemTypes.SELECTION:
        case BaseItemTypes.LIST:
        case BaseItemTypes.PROJECTION:
          params = {
            TableElementIdentity: {
              ElementId: arg.Id,
              Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : null,
              Destination: arg.DestType,
              Kind: getIdentityKind(arg)
            } as AnalyticsElementIdentity,
            WriteActionsCount: writeCount,
            ReadActionsCount: readCount
          };
          http_url = "Analytics/General/get-table-element-details";
          break;
        case BaseItemTypes.GRAPH:
        case BaseItemTypes.PIE_CHART:
        case BaseItemTypes.BAR_CHART:
          params = {
            ChartIdentity: {
              ChartId: arg.Id,
              ChartType: arg.elType === BaseItemTypes.GRAPH ? AnalyticsV2ChartType.Linear :
                (arg.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
              Destination: arg.DestType
            } as AnalyticsChartIdentity,
            WriteActionsCount: writeCount,
            ReadActionsCount: readCount
          };
          http_url = "Analytics/Charts/get-details";
          break;
        case BaseItemTypes.FOLDER:
          params = {
            FolderIdentity: {
              FolderId: arg.FolderId,
              Segment: arg.DestType
            } as AnalyticsFolderIdentity,
            WriteActionsCount: writeCount,
            ReadActionsCount: readCount
          };
          http_url = "Analytics/folders/get-details";
          break;
        default:
          http_url = "";
          break;
      }

      if (!http_url) {
        return { data: undefined }
      }

      try {
        const response = await http.post(http_url, params);

        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async fetchAnalyticsActionsLog(arg: BaseListItem, actionType: AnalyticsActionType, offset: number, count: number) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT, true)

      const body: AnalyticsActionRecordsLogFetchRequest = {
        ElementIdentity: null,
        ChartIdentity: null,
        FolderIdentity: null,
        Offset: offset,
        Count: count,
        ActionType: actionType,
      }

      switch (arg.elType) {
        case BaseItemTypes.SELECTION:
        case BaseItemTypes.LIST:
        case BaseItemTypes.PROJECTION:
          body.ElementIdentity = {
            ElementId: arg.Id,
            Kind: getIdentityKind(arg) as AnalyticsElementKind,
            Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : null,
            Destination: arg.DestType,
          } as AnalyticsElementIdentity
          break
        case BaseItemTypes.GRAPH:
        case BaseItemTypes.PIE_CHART:
        case BaseItemTypes.BAR_CHART:
          body.ChartIdentity = {
            ChartId: arg.Id,
            ChartType: arg.elType === BaseItemTypes.GRAPH ? AnalyticsV2ChartType.Linear :
              (arg.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
            Destination: arg.DestType,
          } as AnalyticsChartIdentity
          break
        case BaseItemTypes.FOLDER:
          body.FolderIdentity = {
            FolderId: arg.FolderId ?? 0,
            Segment: arg.DestType,
          } as AnalyticsFolderIdentity
          break
        default:
          return { data: [] }
      }

      try {
        const response = await http.post("Analytics/actions/fetch-log", body);
        if (response.status === 200) {
          const { data } = response;
          return { data: Array.isArray(data) ? data : [] }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: [] }
    },

    /** Записать активность «Прочитать табличный элемент» (v13.4.3.0). Вызывать при выборе выборки/проекции/списка, когда читаются настройки. */
    async recordAnalyticsReadTableElement(arg: BaseListItem) {
      if (arg.elType !== BaseItemTypes.SELECTION && arg.elType !== BaseItemTypes.LIST && arg.elType !== BaseItemTypes.PROJECTION) return
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT, true)
      const body: AnalyticsElementIdentity = {
        ElementId: arg.Id,
        Kind: getIdentityKind(arg) as AnalyticsElementKind,
        Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : undefined,
        Destination: arg.DestType,
      }
      try {
        await http.post("Analytics/actions/read-table-element", body)
      } catch (e) {
        // fire-and-forget, не ломаем UI
      }
    },

    /** Записать активность «Прочитать график» (v13.4.3.0). Вызывать при выборе графика, когда читаются настройки. */
    async recordAnalyticsReadChart(arg: BaseListItem) {
      if (arg.elType !== BaseItemTypes.GRAPH && arg.elType !== BaseItemTypes.PIE_CHART && arg.elType !== BaseItemTypes.BAR_CHART) return
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT, true)
      const body: AnalyticsChartIdentity = {
        ChartId: arg.Id,
        ChartType: arg.elType === BaseItemTypes.GRAPH ? AnalyticsV2ChartType.Linear :
          (arg.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
        Destination: arg.DestType,
      }
      try {
        await http.post("Analytics/actions/read-chart", body)
      } catch (e) {
        // fire-and-forget
      }
    },

    async changePersonInfo(arg: PersonEditRequest) {
      const rootstore = rootStore()
      const http = new CommonHttp(true)
      try {
        const response = await http.put(`persons/edit`, arg);

        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.table_loading = false
      }

      return { data: undefined }
    },

    async getFilterWorkers() {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/GetFilterEntities/Workers/?segment=${rootstore.generalMode}`);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getAddressContainerStructure() {
      if (this.address_input_container_cache.length) {
        return { data: _.cloneDeep(this.address_input_container_cache) }
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Address/General/GetInputContainer`);
        if (response.status === 200) {
          const { data } = response;
          this.address_input_container_cache = _.cloneDeep(data || []);
          return { data: _.cloneDeep(this.address_input_container_cache) }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async getAddressHints(arg: AddressInputContainer) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.post(`Address/General/GetAutocompleteChains`, arg);
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async callPhoneNumber(phone: string) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.post(`Persons/search/phone/call?phone=${encodeURIComponent(phone)}`, {});
        if (response.status === 200) {
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    async updateSingleTagName(arg: RenameAsker, no_indication: boolean = false) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        http_url = `Tag/Rename`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, arg);

        if (response.status === 200 || response.status === 201) {
          return true
        } else {
          return false
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return false
    },

    async saveTableWidth(entityId: number, width: number) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        http_url = `Analytics/General/EditColumnSettingsWidth/${entityId}`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.get(http_url, width);

        if (response.status === 200 || response.status === 201) {
          return true
        } else {
          return false
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return false
    },

    async updateSingleTagStatus(arg: TagEditStatusAsker, no_indication: boolean = false) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        http_url = `Tag/EditStatus`;

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, arg);

        if (response.status === 200 || response.status === 201) {
          return true
        } else {
          return false;
        }
      } catch (e) {
        console.log(JSON.stringify(e));
      }

      return false;
    },

    // --- MOCK CLIENTS ---
    async getMockClientListAndCount(
      filter: string = "",
      tags: number[] = [],
      count: number = 50
    ) {
      try {
        const { mockClients } = await import("@/mock/clients");
        let allClients = mockClients || [];
        if (filter && filter.trim() !== "") {
          const searchText = filter.toLowerCase();
          allClients = allClients.filter(
            (client) =>
              client.Name.toLowerCase().includes(searchText) ||
              (client.Parameters &&
                client.Parameters.some(
                  (param) =>
                    param.Instance &&
                    param.Instance.Value &&
                    param.Instance.Value.toLowerCase().includes(searchText)
                )) ||
              (client.AddressChain &&
                client.AddressChain.some(
                  (addr) =>
                    typeof addr === "string" &&
                    addr.toLowerCase().includes(searchText)
                ))
          );
        }
        if (tags && tags.length > 0) {
          allClients = allClients.filter(
            (client) =>
              client.Tags && client.Tags.some((tag) => tags.includes(tag.Id))
          );
        }
        const limitedClients = allClients.slice(0, count);
        return {
          data: {
            List: limitedClients,
            Count: allClients.length,
          },
        };
      } catch (e) {
        console.log("Mock data error:", e);
        return { data: { List: [], Count: 0 } };
      }
    },
    async getMockClientGetCount() {
      try {
        const { mockClients } = await import("@/mock/clients");
        const allClients = mockClients || [];
        return {
          data: {
            Count: allClients.length,
          },
        };
      } catch (e) {
        console.log("Mock data error:", e);
        return { data: { Count: 0 } };
      }
    },
  },

  getters: {
    isTableLoading(state): boolean {
      return state.table_loading;
    },

    getTableRowCount(state): number {
      console.log('return '+(state._COUNT_ || process.env.VUE_APP_RESULTS_COUNT))
      return state._COUNT_ || process.env.VUE_APP_RESULTS_COUNT
    },

    getCachedTagContainer(state): TagContainer {
      let t_type: TagEntityType = TagEntityType.Unknown
      const rootstore = rootStore()

      switch (rootstore.generalMode) {
        case AnalyticsV2ElementType.Client:
          t_type = TagEntityType.Client;
          break;
        case AnalyticsV2ElementType.Project:
          t_type = TagEntityType.Project;
          break;
        case AnalyticsV2ElementType.File:
          t_type = TagEntityType.File;
          break;
        case AnalyticsV2ElementType.Person:
          t_type = TagEntityType.Person;
          break;
        case AnalyticsV2ElementType.Job:
          t_type = TagEntityType.Job;
          break;
        default:
          t_type = TagEntityType.Unknown;
          break;
      }
      if (state.container_cache.has(t_type)) {
        // @ts-ignore
        return state.container_cache.get(t_type)
      } else {
        return { AllSystemTags: [], OftenUsedByUserTags: [], OftenUsedForEntityTags: []} as TagContainer
      }
    },

    getDefaultRowCount(state): number {
      return process.env.VUE_APP_RESULTS_COUNT
    },

    getCachedAddressContainer(state): AddressInputComponentOut[] {
      return _.cloneDeep(state.address_input_container_cache || []);
    }
  }
});
